import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import {
  CLIENTS,
  SERVICES,
  HOW_WE_WORK,
  CASE_STUDIES,
  TESTIMONIALS,
  FOUNDER,
  BRAND_ASSETS,
} from '../data/mock';

// Scroll to Top Button Component
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top"
    >
      <ChevronUp size={20} />
    </button>
  );
};

// Hero Section - Cinematic, Luxury Fashion House Feel
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="hero-section">
      {/* Cinematic Background Layers */}
      <div className="hero-vignette" />
      <div className="hero-light-falloff" />
      <div className="hero-ambient-1" />
      <div className="hero-ambient-2" />
      <div className="hero-frame" />

      <div className="container-luxury relative z-10 py-20">
        <div className="max-w-5xl">
          {/* Pre-headline */}
          <div 
            className={`mb-10 transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <span 
              className="label-sm"
              style={{ color: 'var(--velora-gold)' }}
            >
              Global Branding Authority
            </span>
          </div>

          {/* Main Typography - NOT a logo, elegant text */}
          <h1 
            className={`display-hero mb-12 transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
            }`}
            style={{ 
              transitionDelay: '500ms',
              color: 'var(--velora-champagne)',
            }}
          >
            Velora Marketing
          </h1>

          {/* Subheadline */}
          <div 
            className={`space-y-3 mb-14 transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '700ms' }}
          >
            <p 
              className="font-display text-2xl md:text-3xl font-light"
              style={{ color: 'rgba(245, 240, 230, 0.9)' }}
            >
              We shape how the world sees brands.
            </p>
            <p 
              className="font-display text-xl md:text-2xl font-light"
              style={{ color: 'rgba(245, 240, 230, 0.65)' }}
            >
              Across markets. Across cultures. Across continents.
            </p>
          </div>

          {/* Divider */}
          <div 
            className={`mb-14 transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
            style={{ transitionDelay: '900ms', transformOrigin: 'left' }}
          >
            <div className="divider-gold" />
          </div>

          {/* Sub-statement */}
          <p 
            className={`font-display text-lg md:text-xl font-light max-w-xl mb-16 transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ 
              transitionDelay: '1100ms',
              color: 'rgba(245, 240, 230, 0.7)',
              lineHeight: 1.8,
            }}
          >
            Not everything grows by being louder.
            <br />
            Some things grow by being positioned correctly.
          </p>

          {/* CTA - Exclusive Access Feel */}
          <div 
            className={`transition-all duration-[1400ms] ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '1300ms' }}
          >
            <a 
              href="https://wa.me/918208377252?text=Hello%20Ashwini%2C%0AI%20came%20across%20Velora%20and%20would%20like%20to%20request%20access%20to%20explore%20a%20potential%20collaboration."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-access group"
            >
              <span className="relative z-10">Request Access</span>
              <ArrowRight 
                size={14} 
                className="ml-3 relative z-10 transition-transform duration-500 group-hover:translate-x-1" 
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Global Authority Section
const GlobalAuthoritySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.25 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="section-padding section-champagne relative"
    >
      {/* Editorial left border */}
      <div className="absolute top-20 bottom-20 left-8 md:left-16 w-px bg-gradient-to-b from-transparent via-velora-gold/20 to-transparent" />

      <div className="container-luxury">
        <div className="max-w-4xl ml-8 md:ml-12">
          <span 
            className={`label-sm mb-10 block transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ color: 'var(--velora-gold)' }}
          >
            Global Authority
          </span>

          <h2 
            className={`display-md mb-10 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            Velora Marketing operates at the intersection of perception, power, and precision.
          </h2>

          <div 
            className={`divider-gold mb-10 transition-all duration-1000 ${
              isVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
            }`}
            style={{ transitionDelay: '300ms', transformOrigin: 'left' }}
          />

          <p 
            className={`body-editorial text-lg transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '450ms' }}
          >
            Our work spans industries and continents — guiding brands that don't want attention,
            but influence.
          </p>
        </div>
      </div>
    </section>
  );
};

// Services Section - Luxury Editorial Cards
const ServicesSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="section-padding section-ivory relative"
    >
      <div className="container-luxury">
        {/* Section Header */}
        <div className="max-w-2xl mb-20">
          <span 
            className={`label-sm mb-8 block transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ color: 'var(--velora-gold)' }}
          >
            What We Shape
          </span>
          <h2 
            className={`display-md transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            Disciplines of mastery
          </h2>
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((service, index) => (
            <div
              key={service.id}
              className={`service-card transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
              }`}
              style={{ transitionDelay: `${300 + index * 120}ms` }}
            >
              <span 
                className="label-sm mb-5 block"
                style={{ color: 'var(--velora-gold-muted)' }}
              >
                0{index + 1}
              </span>
              <h3 className="heading-lg mb-5 service-card-title">
                {service.title}
              </h3>
              <p className="body-editorial" style={{ color: 'var(--velora-charcoal-light)' }}>
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How We Work Section - Horizontal Narrative Flow
const HowWeWorkSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="section-padding section-green relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-velora-gold/[0.03] to-transparent pointer-events-none" />
      
      <div className="container-luxury relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span 
            className={`label-sm mb-8 block transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ color: 'var(--velora-gold)' }}
          >
            The Process
          </span>
          <h2 
            className={`display-md transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ color: 'var(--velora-ivory)', transitionDelay: '150ms' }}
          >
            How We Work
          </h2>
        </div>

        {/* Narrative Flow - Each step is a chapter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-4">
          {HOW_WE_WORK.map((step, index) => (
            <div
              key={step.step}
              className={`process-chapter transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${300 + index * 180}ms` }}
            >
              <span 
                className="block font-display text-6xl md:text-7xl mb-8"
                style={{ color: 'var(--velora-gold)', opacity: 0.35 }}
              >
                {step.step}
              </span>
              <h3 
                className="heading-md mb-5"
                style={{ color: 'var(--velora-ivory)' }}
              >
                {step.title}
              </h3>
              <p 
                className="body-md"
                style={{ color: 'var(--velora-champagne)', opacity: 0.7 }}
              >
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Clients Section - Slow Seamless Marquee with Better Spacing
const ClientsSection = () => {
  return (
    <section className="py-16 md:py-20 section-cream">
      <div className="container-luxury">
        <div className="text-center">
          <span className="label-sm" style={{ color: 'var(--velora-gold-muted)' }}>
            Trusted By
          </span>
        </div>
      </div>
      
      <div className="logo-marquee-container">
        <div className="logo-marquee animate-marquee">
          {[...CLIENTS, ...CLIENTS].map((client, index) => (
            <span
              key={`${client}-${index}`}
              className="font-display text-2xl md:text-3xl px-10 md:px-16 whitespace-nowrap"
              style={{ 
                color: 'var(--velora-green-deep)', 
                opacity: 0.28,
              }}
            >
              {client}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section - Sliding Editorial Cards
const CaseStudiesSection = () => {
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.offsetWidth * 0.65;
      sliderRef.current.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      ref={sectionRef}
      className="section-padding section-champagne"
    >
      <div className="container-luxury">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-14">
          <div>
            <span 
              className={`label-sm mb-6 block transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: 'var(--velora-gold)' }}
            >
              Selected Work
            </span>
            <h2 
              className={`display-md transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '150ms' }}
            >
              Case Studies
            </h2>
          </div>
          <div 
            className={`flex items-center space-x-2 mt-8 md:mt-0 transition-all duration-1000 ${
              isVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <button 
              onClick={() => scrollSlider('prev')}
              className="case-nav-btn"
              aria-label="Previous"
            >
              <ChevronLeft size={16} style={{ color: 'var(--velora-green-deep)' }} />
            </button>
            <button 
              onClick={() => scrollSlider('next')}
              className="case-nav-btn"
              aria-label="Next"
            >
              <ChevronRight size={16} style={{ color: 'var(--velora-green-deep)' }} />
            </button>
          </div>
        </div>

        {/* Sliding Cards */}
        <div ref={sliderRef} className="case-slider -mx-4 px-4">
          {CASE_STUDIES.map((study, index) => (
            <Link
              key={study.id}
              to="/work"
              className={`case-card group transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
              }`}
              style={{ transitionDelay: `${350 + index * 100}ms` }}
            >
              <div 
                className="aspect-[4/3] mb-6 relative overflow-hidden flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(145deg, var(--velora-green-deep) 0%, var(--velora-green) 100%)' 
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-velora-gold/[0.05] to-transparent" />
                <p 
                  className="font-display text-lg md:text-xl italic text-center px-8 relative z-10 leading-relaxed"
                  style={{ color: 'rgba(245, 240, 230, 0.85)' }}
                >
                  "{study.cardStatement}"
                </p>
              </div>
              <h3 className="heading-lg mb-3 group-hover:text-velora-gold transition-colors duration-500">
                {study.title}
              </h3>
              <p className="body-md mb-5" style={{ color: 'var(--velora-charcoal-light)' }}>
                {study.description}
              </p>
              <span 
                className="inline-flex items-center text-sm font-medium transition-all duration-400"
                style={{ color: 'var(--velora-green-deep)' }}
              >
                View Case Study
                <ArrowRight size={14} className="ml-2 transition-transform duration-400 group-hover:translate-x-2" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  const startAutoPlay = () => {
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 7000);
  };

  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(intervalRef.current);
  }, []);

  const goTo = (index) => {
    clearInterval(intervalRef.current);
    setCurrentIndex(index);
    startAutoPlay();
  };

  return (
    <section className="section-padding section-ivory">
      <div className="container-luxury">
        <div className="text-center mb-14">
          <span className="label-sm" style={{ color: 'var(--velora-gold)' }}>
            Client Perspective
          </span>
        </div>

        <div className="max-w-4xl mx-auto relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-[800ms] ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {TESTIMONIALS.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="w-full flex-shrink-0 px-4 md:px-20 text-center"
                >
                  <blockquote 
                    className="display-md italic mb-12" 
                    style={{ color: 'var(--velora-green-deep)' }}
                  >
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="divider-gold mx-auto mb-6" />
                  <p className="label-sm" style={{ color: 'var(--velora-gold)' }}>
                    {testimonial.author}
                  </p>
                  <p className="body-md mt-2" style={{ color: 'var(--velora-charcoal-light)' }}>
                    {testimonial.role}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Dots Navigation */}
          <div className="flex justify-center items-center mt-14 space-x-3">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`transition-all duration-600 ${
                  index === currentIndex
                    ? 'w-10 h-[3px] bg-velora-gold'
                    : 'w-3 h-[3px] bg-velora-green-deep/15 hover:bg-velora-gold/40'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Founder Section - Two Column, Timeless
const FounderSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="section-padding section-cream"
    >
      <div className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Image Column */}
          <div 
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="founder-image-wrapper">
              <img
                src={FOUNDER.image}
                alt={FOUNDER.name}
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
          </div>

          {/* Text Column */}
          <div 
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <h2 className="display-md mb-3">{FOUNDER.name}</h2>
            <p 
              className="body-md mb-10"
              style={{ color: 'var(--velora-gold-muted)' }}
            >
              {FOUNDER.title}
            </p>
            <div className="divider-gold mb-10" />
            <p 
              className="body-editorial text-xl italic mb-12"
              style={{ color: 'var(--velora-green-deep)' }}
            >
              "{FOUNDER.philosophy}"
            </p>
            <Link 
              to="/perspective" 
              className="inline-flex items-center body-md hover:text-velora-gold transition-colors duration-400 group"
              style={{ color: 'var(--velora-green-deep)' }}
            >
              Read full perspective
              <ArrowRight size={14} className="ml-2 transition-transform duration-400 group-hover:translate-x-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section - Reduced Height, Visible CTA Button
const FinalCTASection = () => {
  return (
    <section className="section-cta-final text-center relative overflow-hidden">
      {/* Subtle ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-gradient-to-b from-velora-gold/[0.04] to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container-luxury max-w-3xl relative z-10">
        <h2
          className="font-display text-3xl md:text-4xl mb-4"
          style={{ color: 'var(--velora-ivory)', fontWeight: 400, letterSpacing: '-0.01em' }}
        >
          This is not for everyone.
        </h2>
        <p
          className="font-display text-xl md:text-2xl mb-2"
          style={{ color: 'var(--velora-champagne)', opacity: 0.8 }}
        >
          If it resonates,
        </p>
        <p
          className="font-display text-xl md:text-2xl mb-8"
          style={{ color: 'var(--velora-champagne)', opacity: 0.8 }}
        >
          you already understand why.
        </p>
        <a
          href="https://wa.me/918208377252?text=Hello%20Ashwini%2C%0AI%20came%20across%20Velora%20and%20would%20like%20to%20request%20access%20to%20explore%20a%20potential%20collaboration."
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-8 py-3.5 font-medium tracking-wider uppercase text-xs transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(196,169,98,0.35)]"
          style={{
            background: 'var(--velora-gold)',
            color: 'var(--velora-green-deep)',
          }}
          onMouseEnter={(e) => e.target.style.background = 'var(--velora-gold-light)'}
          onMouseLeave={(e) => e.target.style.background = 'var(--velora-gold)'}
        >
          Request Access
          <ArrowRight size={14} className="ml-3" />
        </a>
      </div>
    </section>
  );
};

// Main Home Page Component
const HomePage = () => {
  return (
    <main>
      <HeroSection />
      <GlobalAuthoritySection />
      <ServicesSection />
      <HowWeWorkSection />
      <ClientsSection />
      <CaseStudiesSection />
      <TestimonialsSection />
      <FounderSection />
      <FinalCTASection />
      <ScrollToTop />
    </main>
  );
};

export default HomePage;
